<script setup lang="ts" generic="T extends FormChoiceValue">
// Types
import type { FormChoiceEntry, FormChoiceValue } from "../types/formselect";

const { fullWidthOptions, options = [] } = defineProps<{
  /**
   * Whether to display the options in full width.
   * @default false
   * @type {boolean}
   */
  fullWidthOptions?: boolean;
  /**
   * The options to display for the radio buttons.
   * @type {FormChoiceEntry<T>[]}
   */
  options: FormChoiceEntry<T>[];
}>();

const field = useFormControl<T | undefined>();

if (!field)
  throw new Error(
    "[FormRadio]: field instance not available. Did you wrap this component inside a FormControl?",
  );

const checkedValue = defineModel<T>({
  set(value) {
    field.setValue(value || undefined, field.validateOnValueUpdate);

    return value;
  },
});

if (field.value.value) {
  checkedValue.value = field.value.value;
}

defineExpose({
  field,
});
</script>

<template>
  <div
    :class="[
      'form-radio',
      {
        'form-radio--full-width-options': fullWidthOptions,
      },
    ]"
  >
    <label
      v-for="(option, key) in options"
      :key="key"
      :class="[
        'form-radio__label',
        {
          'form-radio__label--disabled': option.disabled,
        },
      ]"
      :for="`${option.value}`"
    >
      <input
        v-model="checkedValue"
        :id="`${option.value}`"
        class="form-radio__native-element u-visually-hidden"
        :disabled="option.disabled"
        type="radio"
        :value="option.value"
      />
      <div class="form-radio__ui-element">
        <div class="form-radio__ui-element-backdrop"></div>
        <div class="form-radio__ui-checked-element"></div>
        <div class="form-radio__ui-element-border"></div>
      </div>
      {{ option.label }}
    </label>
  </div>
</template>

<style scoped lang="scss">
/* stylelint-disable selector-class-pattern */
/* stylelint-disable no-descending-specificity */

/* TODO: fix all the specificity issues */
.form-radio {
  order: 1;

  &__label {
    display: inline-flex;
    gap: var(--form-radio-label-gap, 0.5rem);
    margin-block: var(--form-radio-offset, 0.5rem);

    & + & {
      margin-inline: var(--form-radio-label-gap, 0.5rem);
    }
  }

  &__label:hover &__ui-element-backdrop,
  &__native-element:focus ~ &__ui-element &__ui-element-backdrop {
    opacity: 1;
  }

  &__ui-element {
    position: relative;
    width: 1em;
    height: 1em;
    font-size: var(--form-radio-ui-element-size, 20px);
  }

  &__ui-element-border {
    position: relative;
    display: block;
    box-sizing: border-box;
    width: 1em;
    height: 1em;
    border: var(--form-radio-ui-element-border-size, 0.12em) solid
      var(--form-radio-ui-element-border-color, #333);
    border-radius: 50%;
  }

  &__ui-element-backdrop {
    position: absolute;
    inset: -3px;
    background-color: var(--form-radio-ui-element-backdrop-color, #f3f3f3);
    border-radius: 50%;
    opacity: 0;
    transition-timing-function: ease-in-out;
    transition-duration: 0.2s;
    transition-property: opacity, transform;
  }

  &__ui-checked-element {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1em;
    height: 1em;
    background-color: var(
      --form-radio-ui-checked-element-placeholder-color,
      #e6e6e6
    );
    border-radius: 50%;
    transform: translate(-50%, -50%) scale(0.4);
    transform-origin: center;
    opacity: 0;
    transition-timing-function: ease-in-out;
    transition-duration: 0.2s;
    transition-property: opacity, background-color, transform;
  }

  &__native-element:checked ~ &__ui-element &__ui-checked-element,
  &__native-element:focus ~ &__ui-element &__ui-checked-element,
  &__label:hover &__ui-element &__ui-checked-element {
    transform: translate(-50%, -50%) scale(0.5);
    opacity: 1;
  }

  &__label:hover input:not(:checked) ~ &__ui-element &__ui-checked-element {
    background-color: var(
      --form-radio-ui-checked-element-placeholder-color,
      #e6e6e6
    );
  }

  &__native-element:checked ~ &__ui-element &__ui-checked-element,
  &__label:hover input:checked ~ &__ui-element &__ui-checked-element {
    background-color: var(--form-radio-ui-checked-element-color, #03d386);
  }

  /* Variants */
  &--full-width-options &__label {
    display: flex;
  }

  &--full-width-options &__label + &__label {
    margin-inline: 0;
  }

  /* States */
  &__label--disabled {
    --form-radio-ui-element-border-color: var(
      --form-element-disabled-color,
      #a0a0a0
    );

    color: var(--form-element-disabled-color, #a0a0a0);
    cursor: not-allowed;
  }

  &__label--disabled &__ui-element-backdrop,
  &__label--disabled &__ui-checked-element {
    display: none;
  }
}
</style>
